import { useMediaQuery } from "@mui/material";
import { useRef } from "react";

const useDrawRadial = (value: number) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const animationSpeed = 0.01; // 게이지 차는 애니메이션 속도
  const minValue = 0; // 최소 0 고정
  const maxValue = 100; // 최대 100 고정

  const isMobile = useMediaQuery("(max-width:900px)");
  let animationProgress = 0; // 애니메이션 진행 상태

  // Canvas 및 Chart 관련 함수
  const drawChart = () => {
    if (!canvas.current) return;

    const parent = canvas.current?.parentElement;
    if (!parent) return;

    const size = Math.min(
      parent.clientWidth,
      parent.clientHeight,
      isMobile ? 140 : 227
    ); // 최대 크기 제한
    const margin = 0;
    const dpr = window.devicePixelRatio || 1;

    canvas.current.width = (size + margin * 2) * dpr;
    canvas.current.height = (size + margin * 2) * dpr;
    canvas.current.style.width = `${size + margin * 2}px`;
    canvas.current.style.height = `${size + margin * 2}px`;

    const ctx = canvas.current.getContext("2d");
    if (!ctx) return;

    ctx.scale(dpr, dpr);

    const centerX = canvas.current.width / (2 * dpr);
    const centerY = canvas.current.height / (2 * dpr);
    const ratio = size / 270;
    const gaugeRadius = 50 * ratio;
    const markerRadius = 15 * ratio;
    const trackWidth = 25 * ratio;
    const trackSpacing = isMobile ? 10 : 20 * ratio;

    const drawGauge = (value: number, color: string) => {
      const minAngle = (140 * Math.PI) / 180; // 7시 방향
      const maxAngle = (400 * Math.PI) / 180; // 5시 방향
      const angleRange = maxAngle - minAngle;
      const anglePerValue = angleRange / (maxValue - minValue);

      const startAngle = minAngle;
      const endAngle = startAngle + value * anglePerValue * animationProgress;

      // 배경 트랙 그리기
      ctx.beginPath();
      ctx.arc(
        centerX,
        centerY,
        gaugeRadius + (trackWidth + trackSpacing),
        startAngle,
        maxAngle
      );
      ctx.strokeStyle = "#64CFF6";
      ctx.lineWidth = trackWidth;
      ctx.stroke();
      ctx.closePath();
      // ✅ 배경 트랙 시작 부분 (7시 방향) 원 추가
      const trackStartX =
        centerX +
        (gaugeRadius + (trackWidth + trackSpacing)) * Math.cos(startAngle);
      const trackStartY =
        centerY +
        (gaugeRadius + (trackWidth + trackSpacing)) * Math.sin(startAngle);

      ctx.beginPath();
      ctx.arc(trackStartX, trackStartY, trackWidth / 2, 0, 2 * Math.PI); // 바깥 원
      ctx.fillStyle = "#64CFF6"; // 트랙 색상과 동일하게
      ctx.fill();
      ctx.closePath();

      // 배경 마지막 원 커스텀
      const trackEndX =
        centerX +
        (gaugeRadius + (trackWidth + trackSpacing)) * Math.cos(maxAngle);
      const trackEndY =
        centerY +
        (gaugeRadius + (trackWidth + trackSpacing)) * Math.sin(maxAngle);

      ctx.beginPath();
      ctx.arc(trackEndX, trackEndY, markerRadius, 0, 2 * Math.PI); // 바깥 원
      ctx.fillStyle = "#64CFF6"; // 트랙 색상과 동일하게
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(trackEndX, trackEndY, markerRadius * 0.15, 0, 2 * Math.PI); // 안쪽 원
      ctx.fillStyle = "white"; // 도넛 내부 색 (배경색과 맞춰야 함)
      ctx.fill();
      ctx.closePath();

      // 원 가운데 글자
      ctx.font = `700 ${isMobile ? "20px" : "24px"} Pretendard`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#333"; // 글자 색상
      ctx.fillText(`${formattedPercentage(100 - value)}%`, centerX, centerY);

      if (value <= 0) return;

      // unlock 트랙 그리기
      ctx.beginPath();
      ctx.arc(
        centerX,
        centerY,
        gaugeRadius + (trackWidth + trackSpacing),
        startAngle,
        Math.min(endAngle, maxAngle) // 최대 각도 제한
      );
      ctx.strokeStyle = color;
      ctx.lineWidth = trackWidth;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.closePath();

      // unlock 마지막 원 커스텀
      const markerX =
        centerX +
        (gaugeRadius + (trackWidth + trackSpacing)) *
          Math.cos(Math.min(endAngle, maxAngle));
      const markerY =
        centerY +
        (gaugeRadius + (trackWidth + trackSpacing)) *
          Math.sin(Math.min(endAngle, maxAngle));

      ctx.beginPath();
      ctx.arc(markerX, markerY, markerRadius, 0, 2 * Math.PI); // 바깥 원
      ctx.fillStyle = color;
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(markerX, markerY, markerRadius * 0.15, 0, 2 * Math.PI); // 안쪽 원
      ctx.fillStyle = "white"; // 도넛 내부 색 (배경색과 맞춰야 함)
      ctx.fill();
      ctx.closePath();
    };

    // 애니메이션 함수
    const animate = () => {
      if (!canvas.current) return;

      if (animationProgress < 1) {
        animationProgress += animationSpeed;

        ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);

        drawGauge(100 - value, "#793AFF");
        requestAnimationFrame(animate);
      } else {
        drawGauge(100 - value, "#793AFF");
      }
    };

    animate();
  };

  const formattedPercentage = (percentage: number) =>
    percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1);

  return {
    canvas,
    drawChart,
    formattedPercentage,
  };
};

export default useDrawRadial;
